import type { RegistrationField } from '../schemas/registration';
import { isIndividualSport } from './sportCategory';

// Field definitions — each typed against the canonical RegistrationField
// shape so apps/admin and apps/app see one source of truth.

const GENDER_FIELD: RegistrationField = {
  name: 'Gender',
  fieldId: 'gender',
  fieldType: 'select',
  primitiveType: 'string',
  helperText: 'Used to match you to gender-restricted divisions',
  required: false,
  options: ['Male', 'Female'],
};
export const BIRTH_DATE_FIELD: RegistrationField = {
  name: 'Birth Date',
  fieldId: 'birth_date',
  fieldType: 'date',
  primitiveType: 'date',
  helperText: 'Your date of birth',
  required: false,
  options: [],
};
export const BIRTH_YEAR_FIELD: RegistrationField = {
  name: 'Birth Year',
  fieldId: 'birth_year',
  fieldType: 'year',
  primitiveType: 'number',
  helperText: 'Year you were born (used for age divisions; computed against Dec 31 of the event year)',
  required: false,
  options: [],
};
const PHONE_FIELD: RegistrationField = {
  name: 'Phone Number',
  fieldId: 'phone',
  fieldType: 'phone',
  primitiveType: 'string',
  helperText: 'Your mobile number',
  required: false,
  options: [],
};
const EMERGENCY_NAME_FIELD: RegistrationField = {
  name: 'Emergency Contact Name',
  fieldId: 'emergency_contact_name',
  fieldType: 'textbox',
  primitiveType: 'string',
  helperText: 'Who should we contact in an emergency?',
  required: false,
  options: [],
};
const EMERGENCY_PHONE_FIELD: RegistrationField = {
  name: 'Emergency Contact Phone',
  fieldId: 'emergency_contact_phone',
  fieldType: 'phone',
  primitiveType: 'string',
  helperText: 'Their phone number',
  required: false,
  options: [],
};
export const SELF_RATING_FIELD: RegistrationField = {
  name: 'Self-Rating',
  fieldId: 'self_rating',
  fieldType: 'textbox',
  primitiveType: 'string',
  helperText: 'Your self-assessed skill rating (e.g. 3.5)',
  required: false,
  options: [],
};
export const DUPR_FIELD: RegistrationField = {
  name: 'DUPR ID',
  fieldId: 'dupr_id',
  fieldType: 'textbox',
  primitiveType: 'string',
  helperText: 'Your DUPR account ID (e.g. 1234567)',
  required: false,
  options: [],
};
const ROLE_FIELD: RegistrationField = {
  name: 'I am a…',
  fieldId: 'role',
  fieldType: 'select',
  primitiveType: 'string',
  helperText: 'Used to gate division eligibility: only Players get matched against gender / age / rating constraints',
  required: false,
  options: ['Player', 'Coach', 'Parent', 'Other'],
};

// Public catalog

export const COMMON_PROFILE_FIELDS: readonly RegistrationField[] = [
  GENDER_FIELD,
  BIRTH_DATE_FIELD,
  BIRTH_YEAR_FIELD,
  PHONE_FIELD,
  EMERGENCY_NAME_FIELD,
  EMERGENCY_PHONE_FIELD,
];

// Per-individual-sport additions. Per design intent, only individual sports
// are keyed here; team-sport additions sit in TEAM_SPORT_PROFILE_FIELDS.
export const INDIVIDUAL_SPORT_PROFILE_FIELDS: Record<string, readonly RegistrationField[]> = {
  pickleball: [SELF_RATING_FIELD, DUPR_FIELD],
  tennis: [SELF_RATING_FIELD],
  golf: [],
};

export const TEAM_SPORT_PROFILE_FIELDS: readonly RegistrationField[] = [ROLE_FIELD];

/**
 * Returns the catalog of premade profile fields admins can include in
 * their Create Profile form for a given org sport. Common fields apply
 * to every sport; the per-sport additions slot in for individual sports
 * (Self-Rating / DUPR), and team sports get the role question.
 */
export function getPremadeProfileFields(sport: string | null | undefined): RegistrationField[] {
  const sportLower = sport ? String(sport).toLowerCase() : '';
  const extras = isIndividualSport(sportLower)
    ? (INDIVIDUAL_SPORT_PROFILE_FIELDS[sportLower] || [])
    : TEAM_SPORT_PROFILE_FIELDS;
  return [...COMMON_PROFILE_FIELDS, ...extras];
}

// Field-id constants for callers that need to reference specific fields
// without depending on the catalog ordering.
export const PROFILE_FIELD_IDS = {
  GENDER: 'gender',
  BIRTH_DATE: 'birth_date',
  BIRTH_YEAR: 'birth_year',
  PHONE: 'phone',
  EMERGENCY_CONTACT_NAME: 'emergency_contact_name',
  EMERGENCY_CONTACT_PHONE: 'emergency_contact_phone',
  SELF_RATING: 'self_rating',
  DUPR_ID: 'dupr_id',
  ROLE: 'role',
} as const;
